import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getPlatformPresenceClientEnv, listPlatformDominios } from "../../../api/client";
import { useAuthContext } from "../../../context/AuthContext";
import { useTeamSocketPresence } from "../../../hooks/useTeamSocketPresence";
import {
  MAX_PLATFORM_PROBE_SOCKETS,
  normalizeWorkspaceHost,
  useWorkspaceProbeSockets,
} from "../../../hooks/useWorkspaceProbeSockets";
import { mergePresenceDetailWithClientEnv } from "../../../utils/clientEnvironment";
import { getActiveWorkspaceSlug } from "../../../utils/workspaceStorage";
import { useStabilizedPresenceRows } from "../../../hooks/useStabilizedPresenceRows";
import { comparePresenceRows } from "../../../utils/platformPresenceStabilization";

export function initialsFromName(name) {
  const s = String(name || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function pushPresenceRows(rows, workspaceHost, tabsByUserId, namesByUserId, presenceDetailByUserId) {
  if (!workspaceHost) return;
  const det = presenceDetailByUserId || new Map();
  for (const [userId, tabs] of tabsByUserId) {
    if (tabs <= 0) continue;
    rows.push({
      key: `${workspaceHost}::${userId}`,
      workspace_host: workspaceHost,
      user_id: userId,
      nome: namesByUserId.get(userId) || `ID ${userId}`,
      tabs,
      presence_detail: det.get(userId) ?? null,
    });
  }
}

/** No console da plataforma, só considera workspace atual se houver slug de tenant pinado (nunca o host da SPA). */
function resolveConsoleCurrentWorkspaceHost(knownHosts) {
  const slug = String(getActiveWorkspaceSlug() || "")
    .trim()
    .toLowerCase();
  if (!slug) return "";
  const host = normalizeWorkspaceHost(`${slug}.xpro.me`);
  if (!host) return "";
  if (knownHosts?.length && !knownHosts.includes(host)) return "";
  return host;
}

/**
 * Une presença do socket (legado + Xpro 4) com heartbeats Redis do Xpro 4.
 * - Socket = quem está online de verdade (legado só existe aqui).
 * - Redis = enriquece device/browser/versão/tema/path e pode acrescentar Xpro 4 ainda sem snapshot socket.
 * Não usa API de “online por data_ultimo_acesso” (inventava presença falsa).
 */
function mergeSocketAndRedisPresence(socketRows, redisItems) {
  const byKey = new Map();

  for (const row of socketRows) {
    const ws = normalizeWorkspaceHost(row.workspace_host);
    const uid = Number(row.user_id);
    if (!ws || !Number.isFinite(uid)) continue;
    const key = `${ws}::${uid}`;
    byKey.set(key, {
      ...row,
      key,
      workspace_host: ws,
      user_id: uid,
      nome: row.nome || `ID ${uid}`,
      tabs: row.tabs > 0 ? row.tabs : 1,
      presence_detail: row.presence_detail ?? null,
      source: "socket",
    });
  }

  const redisByUser = new Map();
  for (const item of redisItems) {
    const ws = normalizeWorkspaceHost(item.workspace_host);
    const uid = Number(item.user_id);
    if (!ws || !Number.isFinite(uid)) continue;
    const key = `${ws}::${uid}`;
    if (!redisByUser.has(key)) redisByUser.set(key, []);
    redisByUser.get(key).push(item);
  }

  for (const [key, envRows] of redisByUser) {
    const existing = byKey.get(key);
    if (existing) {
      byKey.set(key, {
        ...existing,
        presence_detail: mergePresenceDetailWithClientEnv(existing.presence_detail, envRows),
      });
      continue;
    }
    // Só Xpro 4 envia presence-env; permite aparecer com detalhes mesmo antes do probe.
    const sep = key.lastIndexOf("::");
    const ws = sep >= 0 ? key.slice(0, sep) : key;
    const uid = sep >= 0 ? Number(key.slice(sep + 2)) : NaN;
    if (!ws || !Number.isFinite(uid)) continue;
    byKey.set(key, {
      key,
      workspace_host: ws,
      user_id: uid,
      nome: `ID ${uid}`,
      tabs: envRows.length,
      presence_detail: mergePresenceDetailWithClientEnv(null, envRows),
      source: "redis",
    });
  }

  return [...byKey.values()];
}

export function usePlatformConsoleOnlinePresence({ onLoaded, onOuterError } = {}) {
  const { t } = useTranslation("app");
  const { bearerToken, userData } = useAuthContext();
  const {
    tabsByUserId,
    namesByUserId,
    presenceDetailByUserId,
    socketConnected: mainSocketConnected,
  } = useTeamSocketPresence(userData);

  const [loadingDominios, setLoadingDominios] = useState(true);
  const [dominiosError, setDominiosError] = useState("");
  const [allWorkspaceHosts, setAllWorkspaceHosts] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [clientEnvItems, setClientEnvItems] = useState([]);

  // Refs: a página passa lambdas inline (onLoaded/onOuterError); se entrarem nas deps
  // de loadDominios, cada setState recria o callback e dispara loop em /dominios.
  const onLoadedRef = useRef(onLoaded);
  const onOuterErrorRef = useRef(onOuterError);
  useEffect(() => {
    onLoadedRef.current = onLoaded;
    onOuterErrorRef.current = onOuterError;
  }, [onLoaded, onOuterError]);

  const workspaceHostList = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const h of allWorkspaceHosts) {
      const n = normalizeWorkspaceHost(h);
      if (!n || seen.has(n)) continue;
      seen.add(n);
      out.push(n);
    }
    out.sort();
    return out;
  }, [allWorkspaceHosts]);

  const currentWorkspaceNorm = useMemo(
    () => resolveConsoleCurrentWorkspaceHost(workspaceHostList),
    [workspaceHostList, refreshTick]
  );

  // Sonda todos os hosts; o socket principal só enriquece o tenant pinado (se houver).
  const probeWorkspaceHosts = workspaceHostList;

  const truncatedTotal = workspaceHostList.length > MAX_PLATFORM_PROBE_SOCKETS;

  const loadDominios = useCallback(async () => {
    setLoadingDominios(true);
    setDominiosError("");
    try {
      const res = await listPlatformDominios({
        limit: 220,
        tenantLegacyStats: "0",
      });
      const items = Array.isArray(res?.items) ? res.items : [];
      const hosts = items
        .map((row) => row?.dominio)
        .filter((d) => {
          const n = normalizeWorkspaceHost(d);
          return Boolean(n);
        });
      setAllWorkspaceHosts(hosts);
      onLoadedRef.current?.();
    } catch (err) {
      setDominiosError(err?.message || t("platformPage.onlineLoadError"));
      setAllWorkspaceHosts([]);
      onOuterErrorRef.current?.();
    } finally {
      setLoadingDominios(false);
    }
  }, [t]);

  useEffect(() => {
    loadDominios();
  }, [loadDominios, refreshTick]);

  const { presenceByWorkspace, socketStats, hostsProbed } = useWorkspaceProbeSockets(probeWorkspaceHosts, {
    bearerToken,
    userId: userData?.id,
    enabled: Boolean(bearerToken && userData?.id && !loadingDominios && !dominiosError && probeWorkspaceHosts.length),
  });

  const socketOnlineItems = useMemo(() => {
    const rows = [];
    if (mainSocketConnected && currentWorkspaceNorm) {
      pushPresenceRows(rows, currentWorkspaceNorm, tabsByUserId, namesByUserId, presenceDetailByUserId);
    }
    for (const [ws, payload] of presenceByWorkspace) {
      const host = normalizeWorkspaceHost(ws);
      if (host && host === currentWorkspaceNorm && mainSocketConnected) continue;
      const tabs = payload.tabs;
      const names = payload.names;
      const detail = payload.detail || new Map();
      pushPresenceRows(rows, ws, tabs, names, detail);
    }
    return rows;
  }, [
    mainSocketConnected,
    currentWorkspaceNorm,
    tabsByUserId,
    namesByUserId,
    presenceDetailByUserId,
    presenceByWorkspace,
  ]);

  const loadClientEnv = useCallback(async () => {
    const hosts = workspaceHostList.slice(0, MAX_PLATFORM_PROBE_SOCKETS);
    if (!hosts.length) {
      setClientEnvItems([]);
      return;
    }
    try {
      const res = await getPlatformPresenceClientEnv({ workspaces: hosts.join(",") });
      setClientEnvItems(Array.isArray(res?.items) ? res.items : []);
    } catch {
      setClientEnvItems([]);
    }
  }, [workspaceHostList]);

  useEffect(() => {
    if (loadingDominios || dominiosError) return undefined;
    loadClientEnv();
    const id = setInterval(loadClientEnv, 25_000);
    return () => clearInterval(id);
  }, [loadClientEnv, loadingDominios, dominiosError, refreshTick]);

  const onlineItemsRaw = useMemo(
    () => mergeSocketAndRedisPresence(socketOnlineItems, clientEnvItems),
    [socketOnlineItems, clientEnvItems]
  );

  // Segunda passagem: garante merge Redis mesmo se a linha já veio com xpro4Tabs do socket
  // (preenche device/browser/versão/tema que o socket às vezes não repassa).
  const onlineItemsMerged = useMemo(() => {
    const clientEnvByWorkspaceUser = new Map();
    for (const row of clientEnvItems) {
      const ws = normalizeWorkspaceHost(row.workspace_host);
      const uid = Number(row.user_id);
      if (!ws || !Number.isFinite(uid)) continue;
      const key = `${ws}::${uid}`;
      if (!clientEnvByWorkspaceUser.has(key)) clientEnvByWorkspaceUser.set(key, []);
      clientEnvByWorkspaceUser.get(key).push(row);
    }

    return onlineItemsRaw.map((row) => {
      const ws = normalizeWorkspaceHost(row.workspace_host);
      const uid = Number(row.user_id);
      const envRows = clientEnvByWorkspaceUser.get(`${ws}::${uid}`) || [];
      if (!envRows.length) return row;
      return {
        ...row,
        presence_detail: mergePresenceDetailWithClientEnv(row.presence_detail, envRows),
      };
    });
  }, [onlineItemsRaw, clientEnvItems]);

  const { rows: onlineItems, stats: onlinePresenceStats } = useStabilizedPresenceRows(onlineItemsMerged);

  const probesAllConnected =
    probeWorkspaceHosts.length === 0 ||
    (socketStats.total > 0 && Number(socketStats.settled || 0) >= socketStats.total);

  const workspacesOnline = useMemo(() => {
    const byHost = new Map();
    for (const row of onlineItems) {
      const host = String(row.workspace_host || "");
      if (!byHost.has(host)) byHost.set(host, []);
      byHost.get(host).push(row);
    }
    const list = [...byHost.entries()].map(([workspace_host, users]) => {
      const sortedUsers = [...users].sort(comparePresenceRows);
      const activeCount = sortedUsers.filter((u) => !u.presenceStale).length;
      const staleCount = sortedUsers.length - activeCount;
      return {
        workspace_host,
        users: sortedUsers,
        count: activeCount,
        staleCount,
        totalCount: sortedUsers.length,
      };
    });
    list.sort((a, b) => {
      const c = b.count - a.count;
      if (c !== 0) return c;
      if (b.totalCount !== a.totalCount) return b.totalCount - a.totalCount;
      return String(a.workspace_host).localeCompare(String(b.workspace_host), undefined, { sensitivity: "base" });
    });
    return list;
  }, [onlineItems]);

  const refreshList = useCallback(() => {
    setRefreshTick((x) => x + 1);
  }, []);

  return {
    loadingDominios,
    dominiosError,
    workspaceHostList,
    truncatedTotal,
    mainSocketConnected,
    socketStats,
    hostsProbed,
    probesAllConnected,
    probeWorkspaceHosts,
    onlineItems,
    onlinePresenceStats,
    workspacesOnline,
    refreshList,
  };
}
